import React, { useState } from 'react'
import classnames from 'classnames/bind'
import { useMutation } from 'react-query'

// Libs
import { NotificationItem } from '@/hooks/useNotificationList'
import { readNotification } from '@/api/readNotification'

// Components
import Icon from '@/assets/icons'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

interface NotificationListProps {
  notificationList: NotificationItem[]
  hasUserData: boolean
  isOpen: boolean
  onRefetch: () => void
}

const NotificationList: React.FC<NotificationListProps> = ({ notificationList, hasUserData, isOpen, onRefetch }) => {
  const [expandedNotifications, setExpandedNotifications] = useState<Set<string>>(new Set())

  const readNotificationMutation = useMutation(readNotification, {
    onSuccess: () => {
      onRefetch()
    },
  })

  const handleNotificationClick = async (notificationId: string) => {
    try {
      await readNotificationMutation.mutateAsync({ notificationIds: [notificationId] })
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  const handleToggleExpand = (e: React.MouseEvent, notificationId: string, isRead: boolean) => {
    e.stopPropagation()
    setExpandedNotifications((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId)
      } else {
        newSet.add(notificationId)
        if (!isRead) {
          handleNotificationClick(notificationId)
        }
      }
      return newSet
    })
  }

  // 检测内容是否超过两行，直接操作 DOM 不触发 re-render
  const handleContentRef = (el: HTMLDivElement | null) => {
    if (!el) return

    // 比较 scrollHeight 和 clientHeight 判断是否溢出
    // CSS 已经设置了 line-clamp: 2，超过 2 行时 scrollHeight > clientHeight
    if (el.scrollHeight > el.clientHeight) {
      el.parentElement?.classList.add('has-overflow')
    }
  }

  return (
    <ul
      onClick={(e) => e.stopPropagation()}
      className={cx('notification-list', {
        'notification-list--open': isOpen,
      })}
    >
      {hasUserData &&
        notificationList.length > 0 &&
        notificationList.map((notification: NotificationItem) => (
          <li
            key={notification.id}
            className={cx('notification-list__item', {
              'notification-list__item--read': notification.isRead,
            })}
          >
            <div
              className={cx('notification-list__item-indicator', {
                'notification-list__item-indicator--read': notification.isRead,
              })}
            ></div>
            <div className={cx('notification-list__item-wrapper')} onClick={() => handleNotificationClick(notification.id)}>
              <div className={cx('notification-list__item-header')}>
                <div className={cx('notification-list__item-title')}>{notification.title}</div>
                <button
                  onClick={(e) => handleToggleExpand(e, notification.id, notification.isRead)}
                  className={cx('notification-list__item-expand-btn', {
                    'notification-list__item-expand-btn--expanded': expandedNotifications.has(notification.id),
                  })}
                >
                  <Icon.ArrowDown />
                </button>
              </div>
              <div
                ref={handleContentRef}
                className={cx('notification-list__item-content', {
                  'notification-list__item-content--expanded': expandedNotifications.has(notification.id),
                })}
              >
                {notification.content}
              </div>
              <div className={cx('notification-list__item-time')}>{new Date(notification.actualSentAt).toLocaleString()}</div>
            </div>
          </li>
        ))}
    </ul>
  )
}

export default NotificationList
